import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getImageUrl } from 'constants/constants';
import { getProductDashboardItems, getProductListByBrand } from 'helpers/backend_helper';
import { AppContext } from 'context/AppContext';
import { ChevronRight, Loader2 } from 'lucide-react';

const CustomerDashboard = () => {
  const { user } = useContext(AppContext);
  const [categories, setCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const response = await getProductDashboardItems();
      if (response.success && response.data?.[0]) {
        const data = response.data[0];
        setCategories(data.categories || []);
        setBrands(data.brands || []);
        // Load products from first brand if available
        const firstBrandId = data.brands?.[0]?.id;
        if (firstBrandId) {
          const prodResponse = await getProductListByBrand(firstBrandId);
          if (prodResponse.success && prodResponse.data) {
            setProducts((prodResponse.data as any[]).slice(0, 12));
          }
        }
      }
    } catch (error) {
      console.error('Error loading dashboard data', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="size-12 animate-spin text-custom-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Banner */}
      <div className="rounded-xl bg-gradient-to-r from-custom-500 to-custom-600 px-6 py-8 text-white shadow-lg">
        <h1 className="text-2xl font-bold">
          Welcome back{user?.fullName ? `, ${user.fullName}` : ''}!
        </h1>
        <p className="mt-1 text-custom-100">
          Discover the latest products and deals
        </p>
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-zink-100">
              Shop by Category
            </h2>
            <Link
              to="/customer/catalog"
              className="flex items-center gap-1 text-sm font-medium text-custom-500 hover:text-custom-600"
            >
              View all
              <ChevronRight className="size-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {categories.slice(0, 6).map((cat: any) => (
              <Link
                key={cat.id}
                to={`/customer/products?categoryId=${cat.id}`}
                className="group flex flex-col items-center rounded-lg border border-slate-200 bg-white p-4 transition shadow-sm hover:border-custom-500 hover:shadow-md dark:border-zink-500 dark:bg-zink-700"
              >
                <div className="mb-2 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-slate-100 dark:bg-zink-600">
                  {cat.logo ? (
                    <img
                      src={getImageUrl(cat.logo, 'categoryThumbnail')}
                      alt={cat.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl font-bold text-slate-400">
                      {cat.name?.charAt(0) || '?'}
                    </span>
                  )}
                </div>
                <span className="text-center text-sm font-medium text-slate-700 group-hover:text-custom-500 dark:text-zink-200">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Brands */}
      {brands.length > 0 && (
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-zink-100">
              Our Brands
            </h2>
            <Link
              to="/customer/products"
              className="flex items-center gap-1 text-sm font-medium text-custom-500 hover:text-custom-600"
            >
              View all
              <ChevronRight className="size-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {brands.slice(0, 6).map((brand: any) => (
              <Link
                key={brand.id}
                to={`/customer/products?brandId=${brand.id}`}
                className="group flex flex-col items-center rounded-lg border border-slate-200 bg-white p-4 transition shadow-sm hover:border-custom-500 hover:shadow-md dark:border-zink-500 dark:bg-zink-700"
              >
                <div className="mb-2 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-slate-100 dark:bg-zink-600">
                  {brand.logo ? (
                    <img
                      src={getImageUrl(brand.logo, 'brandThumbnail')}
                      alt={brand.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl font-bold text-slate-400">
                      {brand.name?.charAt(0) || '?'}
                    </span>
                  )}
                </div>
                <span className="text-center text-sm font-medium text-slate-700 group-hover:text-custom-500 dark:text-zink-200">
                  {brand.name}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured Products */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-zink-100">
            New Arrivals
          </h2>
          <Link
            to="/customer/products"
            className="flex items-center gap-1 text-sm font-medium text-custom-500 hover:text-custom-600"
          >
            View all
            <ChevronRight className="size-4" />
          </Link>
        </div>
        {products.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {products.map((product: any) => {
              const variant = product?.productsProductvariants?.[0];
              const imageUrl = product?.imageUrl || variant?.imageUrl || product?.productsProductimages?.[0]?.url;
              return (
                <Link
                  key={product.id}
                  to={`/customer/products/${product.id}`}
                  className="group overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition hover:border-custom-500 hover:shadow-md dark:border-zink-500 dark:bg-zink-700"
                >
                  <div className="aspect-square overflow-hidden bg-slate-100 dark:bg-zink-600">
                    {imageUrl ? (
                      <img
                        src={getImageUrl(imageUrl, 'productThumbnail')}
                        alt={product.name}
                        className="h-full w-full object-cover transition group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-slate-400">
                        <span className="text-4xl">{product.name?.charAt(0) || '?'}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="truncate text-sm font-medium text-slate-800 dark:text-zink-100">
                      {product.name}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-custom-500">
                      ₹{variant?.unitPrice || product.unitPrice || '—'}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 py-12 text-center text-slate-500 dark:border-zink-500 dark:text-zink-400">
            No products available at the moment.
          </div>
        )}
      </section>
    </div>
  );
};

export default CustomerDashboard;
